<template>
  <div v-if="visible" class="ai-panel-overlay" @click.self="$emit('close')">
    <div class="ai-panel-content slide-in-right" :class="{ dark: isDarkMode }">
      <div class="ai-panel-header">
        <h2><i class="fas fa-wand-magic-sparkles"></i> {{ $t('Summarize') }}</h2>
        <button class="ai-close" @click="$emit('close')"><i class="fas fa-times"></i></button>
      </div>

      <!-- Page-aware context banner -->
      <div v-if="pageContext" class="ai-context-banner" :class="pageContext.pageType">
        <i :class="pageContextIcon"></i>
        <span class="ai-context-label">{{ $t('You are on') }} <strong>{{ pageContext.label }}</strong></span>
        <span v-if="elementId" class="ai-context-badge ok">{{ fileName || elementId }}</span>
        <span v-else class="ai-context-badge warn">{{ $t('No file selected') }}</span>
      </div>
      <p v-if="pageContext && !pageContext.supportsTranscriptContext && !elementId" class="ai-hint ai-hint-warn">{{ $t('Summarize is only available on transcript pages — paste text below or open a transcript.') }}</p>

      <!-- Source selector -->
      <div v-if="!elementId" class="ai-section">
        <label class="ai-label">{{ $t('Source') }}</label>
        <p class="ai-hint">{{ $t('Select a transcript to summarize, or paste text below.') }}</p>
        <textarea
          v-model="rawText"
          class="ai-textarea"
          rows="6"
          :placeholder="$t('Paste transcript text here...')"
          :disabled="loading"
        ></textarea>
      </div>
      <div v-else class="ai-section">
        <div class="ai-source-chip">
          <i class="fas fa-file-lines"></i>
          <span class="ai-source-name">{{ fileName || elementId }}</span>
          <span v-if="fileName" class="ai-source-id">{{ elementId.slice(0, 8) }}…</span>
        </div>
      </div>

      <!-- Style -->
      <div class="ai-section">
        <label class="ai-label">{{ $t('Style') }}</label>
        <div class="ai-presets">
          <button
            v-for="opt in styleOptions"
            :key="opt.value"
            type="button"
            class="ai-chip"
            :class="{ active: style === opt.value }"
            :disabled="loading"
            @click="style = opt.value"
          >{{ $t(opt.label) }}</button>
        </div>
      </div>

      <!-- Language (optional) -->
      <div class="ai-section">
        <label class="ai-label">{{ $t('Output language (optional)') }}</label>
        <input v-model="outputLanguage" class="ai-input" :placeholder="$t('e.g. English, Romanian')" :disabled="loading" />
      </div>

      <div v-if="error" class="ai-status ai-status-error">{{ error }}</div>

      <div v-if="loading" class="ai-status ai-status-progress">
        <i class="fas fa-spinner fa-spin"></i> {{ $t('Summarizing...') }}
      </div>

      <!-- Result -->
      <div v-if="result" class="ai-section ai-result">
        <div class="ai-result-header">
          <label class="ai-label">{{ $t('Summary') }}</label>
          <button class="btn btn-sm btn-neutral" @click="copyResult"><i class="fas fa-copy"></i> {{ $t('Copy') }}</button>
        </div>
        <div class="ai-result-body">{{ result.summary }}</div>
        <div v-if="result.truncated" class="ai-hint">{{ $t('Source was truncated for length.') }}</div>
        <div class="ai-meta">{{ result.model }} · {{ result.style }}</div>
      </div>

      <div class="ai-actions">
        <button class="btn btn-cancel" @click="$emit('close')">{{ $t('Close') }}</button>
        <button class="btn btn-fill btn-info" :disabled="loading || !canSummarize" @click="doSummarize">
          <i class="fas fa-wand-magic-sparkles"></i> {{ $t('Summarize') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { storeToRefs } from 'pinia';
import { useThemeStore } from '@stores/themeStore';
import { summarize, type SummarizeStyle } from '@utils/aiTools';
import { notify } from '@components/NotificationPlugin';

export default defineComponent({
  name: 'AiSummarizePanel',
  props: {
    visible: { type: Boolean, default: false },
    elementId: { type: String, default: '' },
    fileName: { type: String, default: '' },
    projectId: { type: String, default: '' },
    outputId: { type: String, default: '' },
    secretId: { type: String, default: '' },
    pageContext: { type: Object as () => { path: string; name: string; pageType: string; label: string; supportsTranscriptContext: boolean } | null, default: null },
  },
  emits: ['close'],
  setup(props) {
    const { t } = useI18n();
    const themeStore = useThemeStore();
    const { isDarkMode } = storeToRefs(themeStore);
    const pageContextIcon = computed(() => {
      const pt = (props.pageContext as any)?.pageType;
      if (pt === 'transcript') return 'fas fa-file-lines';
      if (pt === 'video') return 'fas fa-video';
      if (pt === 'dashboard') return 'fas fa-table-columns';
      return 'fas fa-location-dot';
    });

    const rawText = ref('');
    const style = ref<SummarizeStyle>('brief');
    const outputLanguage = ref('');
    const loading = ref(false);
    const error = ref('');
    const result = ref<any>(null);

    const styleOptions = [
      { value: 'brief', label: 'Brief' },
      { value: 'detailed', label: 'Detailed' },
      { value: 'bullets', label: 'Bullets' },
      { value: 'meeting-notes', label: 'Meeting notes' },
      { value: 'chapters', label: 'Chapters' },
    ] as const;

    const canSummarize = computed(() => {
      if (props.elementId) return true;
      return rawText.value.trim().length >= 20;
    });

    watch(() => props.visible, (v) => {
      if (v) { error.value = ''; result.value = null; }
    });

    const doSummarize = async () => {
      if (!canSummarize.value || loading.value) return;
      loading.value = true;
      error.value = '';
      result.value = null;
      try {
        const res = await summarize({
          elementId: props.elementId || undefined,
          projectId: props.projectId || undefined,
          outputId: props.outputId || undefined,
          secretId: props.secretId || undefined,
          fileName: props.fileName || undefined,
          text: props.elementId ? undefined : rawText.value,
          style: style.value,
          language: outputLanguage.value.trim() || undefined,
        });
        result.value = res;
      } catch (e: any) {
        error.value = e?.response?.data?.message || e?.message || t('Summarization failed');
      } finally {
        loading.value = false;
      }
    };

    const copyResult = async () => {
      if (!result.value?.summary) return;
      try {
        await navigator.clipboard.writeText(result.value.summary);
        notify('success', t('Copied to clipboard'));
      } catch {
        notify('danger', t('Copy failed'));
      }
    };

    return { isDarkMode, rawText, style, styleOptions, outputLanguage, loading, error, result, canSummarize, doSummarize, copyResult, pageContextIcon };
  },
});
</script>

<style scoped>
.ai-panel-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.45); z-index: 9999; display: flex; justify-content: flex-end; }
.ai-panel-content { width: 520px; max-width: 100vw; height: 100vh; background: #fff; overflow-y: auto; padding: 20px; display: flex; flex-direction: column; gap: 16px; box-shadow: -8px 0 32px rgba(0,0,0,0.18); }
.ai-panel-content.dark { background: #1a2233; color: #e2e8f0; }
.slide-in-right { animation: slideIn 0.22s ease; }
@keyframes slideIn { from { transform: translateX(100%); } to { transform: translateX(0); } }
.ai-panel-header { display: flex; justify-content: space-between; align-items: center; }
.ai-panel-header h2 { font-size: 1.15rem; font-weight: 700; display: flex; gap: 8px; align-items: center; }
.ai-close { background: none; border: none; font-size: 1.1rem; cursor: pointer; color: inherit; opacity: 0.7; }
.ai-close:hover { opacity: 1; }
.ai-section { display: flex; flex-direction: column; gap: 8px; }
.ai-label { font-weight: 600; font-size: 0.85rem; opacity: 0.9; }
.ai-hint { font-size: 0.8rem; opacity: 0.65; }
.ai-textarea, .ai-input { width: 100%; border: 1px solid #e6e9f0; border-radius: 8px; padding: 10px 12px; font-size: 0.9rem; background: #f8fafc; color: #1f2937; }
.dark .ai-textarea, .dark .ai-input { background: #212b40; border-color: #2a3550; color: #e2e8f0; }
.ai-presets { display: flex; flex-wrap: wrap; gap: 6px; }
.ai-chip { padding: 6px 12px; border-radius: 999px; border: 1px solid #e6e9f0; background: #fff; font-size: 0.8rem; cursor: pointer; }
.ai-chip.active { background: #4f46e5; color: #fff; border-color: #4f46e5; }
.dark .ai-chip { background: #212b40; border-color: #2a3550; color: #e2e8f0; }
.dark .ai-chip.active { background: #818cf8; color: #0f1420; }
.ai-context-banner { display: flex; align-items: center; gap: 8px; padding: 8px 10px; border-radius: 8px; font-size: 0.8rem; background: #f1f5f9; border: 1px solid #e6e9f0; }
.dark .ai-context-banner { background: #212b40; border-color: #2a3550; }
.ai-context-label { opacity: 0.85; }
.ai-context-badge { margin-left: auto; font-size: 0.7rem; padding: 2px 8px; border-radius: 999px; background: #e0e7ff; color: #4338ca; font-weight: 600; }
.ai-context-badge.ok { background: #dcfce7; color: #166534; }
.ai-context-badge.warn { background: #fef3c7; color: #92400e; }
.dark .ai-context-badge { background: #312e81; color: #c7d2fe; }
.dark .ai-context-badge.ok { background: #14532d; color: #bbf7d0; }
.dark .ai-context-badge.warn { background: #78350f; color: #fde68a; }
.ai-source-chip { display: flex; align-items: center; gap: 8px; padding: 10px 12px; background: #f1f5f9; border-radius: 8px; font-size: 0.85rem; }
.dark .ai-source-chip { background: #212b40; }
.ai-source-name { font-weight: 600; }
.ai-source-id { opacity: 0.5; font-size: 0.75rem; }
.ai-status { padding: 10px 12px; border-radius: 8px; font-size: 0.85rem; }
.ai-status-error { background: rgba(239,68,68,0.12); color: #dc2626; border-left: 4px solid #dc2626; }
.ai-status-progress { background: rgba(79,70,229,0.08); color: #4f46e5; display: flex; gap: 8px; align-items: center; }
.ai-result { background: #f8fafc; border: 1px solid #e6e9f0; border-radius: 10px; padding: 14px; }
.dark .ai-result { background: #212b40; border-color: #2a3550; }
.ai-result-header { display: flex; justify-content: space-between; align-items: center; }
.ai-result-body { white-space: pre-wrap; line-height: 1.6; font-size: 0.9rem; max-height: 50vh; overflow-y: auto; }
.ai-meta { font-size: 0.7rem; opacity: 0.5; margin-top: 6px; }
.ai-actions { display: flex; justify-content: flex-end; gap: 10px; margin-top: auto; padding-top: 12px; border-top: 1px solid #e6e9f0; }
.dark .ai-actions { border-color: #2a3550; }
</style>
